import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, Smile, FileText, X } from 'lucide-react';
import styles from '../Chat.module.css';

const CustomMessageInput = ({ channel }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const quickEmojis = [
    { emoji: '\u{1F600}', label: 'Grinning' },
    { emoji: '\u{1F602}', label: 'Joy' },
    { emoji: '\u{2764}\u{FE0F}', label: 'Heart' },
    { emoji: '\u{1F44D}', label: 'Thumbs Up' },
    { emoji: '\u{1F389}', label: 'Party' },
    { emoji: '\u{1F525}', label: 'Fire' },
    { emoji: '\u{1F4AF}', label: '100' },
    { emoji: '\u{1F44F}', label: 'Clap' },
    { emoji: '\u{1F60D}', label: 'Heart Eyes' },
    { emoji: '\u{1F914}', label: 'Thinking' },
    { emoji: '\u{1F60E}', label: 'Cool' },
    { emoji: '\u{1F64F}', label: 'Pray' },
    { emoji: '\u{1F4AA}', label: 'Strong' },
    { emoji: '\u{2728}', label: 'Sparkles' },
    { emoji: '\u{1F3AF}', label: 'Target' },
    { emoji: '\u{1F680}', label: 'Rocket' },
  ];

  // Typing indicators are driven by real keystrokes only. The SDK throttles
  // typing.start to one per 2s and its client cleaner sends typing.stop ~1s
  // after the last keystroke, so no local timer is needed. (A state-driven
  // effect here used to re-fire keystroke/stopTyping every second while a
  // draft sat in the box, tripping Stream's per-user rate limit.)
  const sendTyping = (fn) => {
    if (!channel) return;
    channel[fn]().catch(() => {});
  };

  useEffect(() => () => {
    if (channel?.isTyping) channel.stopTyping().catch(() => {});
  }, [channel]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if ((!message.trim() && attachments.length === 0) || sending || !channel) return;

    setSending(true);
    try {
      const uploadedAttachments = [];

      for (const attachment of attachments) {
        if (!attachment.file) continue;
        const isImage = attachment.file.type?.startsWith('image/');
        const upload = isImage
          ? await channel.sendImage(attachment.file, attachment.file.name, attachment.file.type)
          : await channel.sendFile(attachment.file, attachment.file.name, attachment.file.type);

        uploadedAttachments.push(isImage
          ? {
              type: 'image',
              title: attachment.file.name,
              image_url: upload.file,
              asset_url: upload.file,
              thumb_url: upload.thumb_url,
              mime_type: attachment.file.type,
              file_size: attachment.file.size,
            }
          : {
              type: 'file',
              title: attachment.file.name,
              asset_url: upload.file,
              mime_type: attachment.file.type,
              file_size: attachment.file.size,
            });
      }

      await channel.sendMessage({
        text: message.trim(),
        attachments: uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
      });
      setMessage('');
      setAttachments([]);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      sendTyping('stopTyping');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleInputChange = (e) => {
    setMessage(e.target.value);
    if (e.target.value) sendTyping('keystroke');
    else if (channel?.isTyping) sendTyping('stopTyping'); // cleared the draft
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 100) + 'px';
    }
  };

  const handleEmojiClick = (emojiChar) => {
    const cursorPosition = textareaRef.current?.selectionStart || message.length;
    const newMessage = message.slice(0, cursorPosition) + emojiChar + message.slice(cursorPosition);
    setMessage(newMessage);
    setShowEmojiPicker(false);

    setTimeout(() => {
      textareaRef.current?.focus();
      const newCursorPosition = cursorPosition + emojiChar.length;
      textareaRef.current?.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newAttachments = files.map(file => ({
      file,
      type: 'file',
      name: file.name,
      size: file.size,
      mimeType: file.type
    }));

    setAttachments([...attachments, ...newAttachments]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className={styles.inputArea}>
      {/* Attachments preview */}
      {attachments.length > 0 && (
        <div className={styles.attachmentBar}>
          {attachments.map((attachment, index) => (
            <div key={index} className={styles.attachmentChip}>
              <FileText size={14} />
              <span>{attachment.name}</span>
              <span style={{ fontSize: 11, color: 'var(--lodge-onyx-300)' }}>({formatFileSize(attachment.size)})</span>
              <button onClick={() => removeAttachment(index)} className={styles.attachmentRemove}>
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input area */}
      <form onSubmit={handleSubmit} className={styles.inputRow}>
        {/* Action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={styles.inputBtn}
            title="Attach file"
          >
            <Paperclip size={18} />
          </button>
          <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} style={{ display: 'none' }} />

          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className={styles.inputBtn}
              title="Add emoji"
            >
              <Smile size={18} />
            </button>

            {showEmojiPicker && (
              <div className={styles.emojiPicker}>
                <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--lodge-onyx-400)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 'var(--sp-2)' }}>
                  Quick Emojis
                </p>
                <div className={styles.emojiGrid}>
                  {quickEmojis.map((item, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleEmojiClick(item.emoji)}
                      className={styles.emojiBtn}
                      title={item.label}
                      style={{ fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif' }}
                    >
                      {item.emoji}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: 'var(--lodge-onyx-300)', textAlign: 'center', marginTop: 'var(--sp-2)', paddingTop: 'var(--sp-2)', borderTop: '1px solid var(--lodge-onyx-100)' }}>
                  Click an emoji to add it to your message
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Message input */}
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleInputChange}
          placeholder="Type a message..."
          className={styles.textarea}
          rows="1"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />

        {/* Send button */}
        <button
          type="submit"
          disabled={(!message.trim() && attachments.length === 0) || sending}
          className={`${styles.sendBtn} ${(message.trim() || attachments.length > 0) ? styles.ready : ''}`}
        >
          {sending ? (
            <div className={styles.spinner} style={{ width: 18, height: 18, borderWidth: 2, borderColor: 'white', borderTopColor: 'transparent' }} />
          ) : (
            <Send size={18} />
          )}
        </button>
      </form>

      <div className={styles.inputHint}>
        Press Enter to send, Shift+Enter for new line
      </div>
    </div>
  );
};

export default CustomMessageInput;
